import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Sparkles, Target } from 'lucide-react';
import { ConversationalInput } from '@/components/shared/ConversationalInput';
import { QuickPrompts } from '@/components/shared/QuickPrompts';
import { useToast } from '@/components/ui/use-toast';

interface OKRItem {
  id: string;
  type: 'objective' | 'key-result';
  title: string;
  description: string;
  prompt: string;
  placeholder: string;
  estimatedMinutes: number;
  content: string;
  completed: boolean;
  parentId?: string;
}

const okrItems: OKRItem[] = [
  {
    id: 'objective-1',
    type: 'objective',
    title: 'Primary Career Objective',
    description: 'Your main career goal for the next quarter.',
    prompt: 'What is your most important career objective for the next 3 months?',
    placeholder: 'Define your primary career objective...',
    estimatedMinutes: 2,
    content: '',
    completed: false
  },
  {
    id: 'kr-1-1',
    type: 'key-result',
    title: 'Key Result #1',
    description: 'First measurable outcome for your objective.',
    prompt: 'What specific, measurable result would prove you\'re achieving your objective?',
    placeholder: 'Define a measurable key result...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    parentId: 'objective-1'
  },
  {
    id: 'kr-1-2',
    type: 'key-result',
    title: 'Key Result #2',
    description: 'Second measurable outcome for your objective.',
    prompt: 'What\'s another way to measure progress toward your career objective?',
    placeholder: 'Define another measurable key result...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    parentId: 'objective-1'
  },
  {
    id: 'objective-2',
    type: 'objective',
    title: 'Secondary Objective',
    description: 'A supporting career goal that complements your primary objective.',
    prompt: 'What secondary career objective would support your main goal?',
    placeholder: 'Define a supporting career objective...',
    estimatedMinutes: 2,
    content: '',
    completed: false
  },
  {
    id: 'kr-2-1',
    type: 'key-result',
    title: 'Key Result #3',
    description: 'Measurable outcome for your secondary objective.',
    prompt: 'How will you measure success for this secondary objective?',
    placeholder: 'Define a measurable key result...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    parentId: 'objective-2'
  }
];

export const ConversationalOKRs: React.FC = () => {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [okrData, setOkrData] = useState(okrItems);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!startTime) {
      setStartTime(new Date());
    }
  }, []);

  const currentItem = okrData[currentItemIndex];
  const completedItems = okrData.filter(item => item.completed).length;
  const progress = (completedItems / okrData.length) * 100;
  const totalEstimatedTime = okrData.reduce((sum, item) => sum + item.estimatedMinutes, 0);
  const timeSpent = startTime ? Math.round((new Date().getTime() - startTime.getTime()) / 60000) : 0;

  const handleContentChange = (content: string) => {
    const updatedItems = [...okrData];
    updatedItems[currentItemIndex] = {
      ...currentItem,
      content,
      completed: content.trim().length > 15
    };
    setOkrData(updatedItems);
  };

  const handleNext = () => {
    if (currentItem.content.trim()) {
      if (currentItemIndex < okrData.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
      } else {
        toast({
          title: "OKRs Complete! 🎯",
          description: `You've defined your career OKRs in ${timeSpent} minutes.`,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentItemIndex > 0) {
      setCurrentItemIndex(currentItemIndex - 1);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    console.log('Quick prompt selected:', prompt);
  };

  const isLastItem = currentItemIndex === okrData.length - 1;
  const canProceed = currentItem.content.trim().length > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header with Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent flex items-center gap-2">
              <Target className="h-6 w-6 text-primary" />
              Career OKRs
            </h1>
            <p className="text-muted-foreground">Define your objectives and key results</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              {completedItems} of {okrData.length} items defined
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{timeSpent}m / ~{totalEstimatedTime}m</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Overall Progress</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* OKR Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {okrData.map((item, index) => (
            <Button
              key={item.id}
              variant={index === currentItemIndex ? "default" : item.completed ? "secondary" : "outline"}
              size="sm"
              onClick={() => setCurrentItemIndex(index)}
              className="whitespace-nowrap flex items-center gap-2"
            >
              {item.completed && <CheckCircle className="h-3 w-3" />}
              <span className="text-xs">
                {item.type === 'objective' ? '🎯' : '📊'} {item.title}
              </span>
            </Button>
          ))}
        </div>
      </div>

      {/* Current Item */}
      <div className="space-y-6">
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <span className="text-lg">
                    {currentItem.type === 'objective' ? '🎯' : '📊'}
                  </span>
                  {currentItem.title}
                </h2>
                <p className="text-muted-foreground">{currentItem.description}</p>
                <Badge 
                  variant={currentItem.type === 'objective' ? 'default' : 'secondary'} 
                  className="mt-2"
                >
                  {currentItem.type === 'objective' ? 'Objective' : 'Key Result'}
                </Badge>
              </div>
              <Badge variant="outline" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                ~{currentItem.estimatedMinutes}m
              </Badge>
            </div>
          </div>
        </Card>

        {/* Quick Prompts */}
        <QuickPrompts
          artifactType="okrs"
          sectionType={currentItem.type}
          onPromptSelect={handleQuickPrompt}
        />

        {/* Conversational Input */}
        <ConversationalInput
          question={currentItem.prompt}
          placeholder={currentItem.placeholder}
          value={currentItem.content}
          onChange={handleContentChange}
          artifactType="okrs"
          sectionType={currentItem.type}
          userContext={`Type: ${currentItem.type}. Completed items: ${completedItems}`}
        />

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentItemIndex === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            <span>Item {currentItemIndex + 1} of {okrData.length}</span>
          </div>

          <Button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex items-center gap-2"
          >
            {isLastItem ? 'Complete OKRs' : 'Next Item'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {!canProceed && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Define this {currentItem.type.replace('-', ' ')} to continue
            </p>
          </div>
        )}
      </div>
    </div>
  );
};